import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import { queryKeys } from "@/app/query/query-keys";
import { unwrap } from "@/app/lib/api-client";
import { haptics } from "@/app/platform";
import type { CreateSessionLogPayload } from "@shared/contracts";
import { logsService } from "./logs.service";
import type { SessionLogPageModel, SessionLogSummaryModel } from "./logs.model";

/**
 * THE LOGS DOMAIN'S SERVER STATE — the write half (BUD-289).
 *
 * ⚠️ History and the benchmarks list (BUD-290) add their READ hooks beside these, against the
 * same `queryKeys.logs` registry entry. The keys were registered up front, by the app shell,
 * for exactly this reason: the two halves were written in parallel and neither had to invent
 * a spelling the other would have to match afterwards.
 *
 * 🔑 NO `meta.ownsErrorMessage` ANYWHERE HERE, AND THAT IS DELIBERATE. Neither of these
 * failures has a field to sit beside — a debrief that would not save is a whole-screen problem
 * and a heart that would not stick is a control-level one, and both are exactly what the query
 * cache's one-message-one-report authority is for. Wording either of them here would produce
 * two toasts for one failure. The `mutationKey` is what makes the report say WHICH write
 * failed: segments 0 and 1 of `["logs", "create"]` are the domain and the action.
 */

/**
 * HISTORY — added by BUD-290, beside the write half rather than in a module of its own.
 *
 * KEYSET, so the cursor lives in the infinite query's PAGE PARAMS and never in the key. A
 * cursor in the key makes every page its own cache entry: `logs.all` then reaches one of
 * them, the rest go stale in place, and the optimistic heart on page one is stranded there
 * while page two carries an untouched copy of the same row.
 */
export function useSessionLogs() {
  return useInfiniteQuery({
    queryKey: queryKeys.logs.list(),
    queryFn: async ({ pageParam }) => unwrap(await logsService.history(pageParam)),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
}

/** The hearted ones — the same rows, filtered by the service, paged the same way. */
export function useBenchmarks() {
  return useInfiniteQuery({
    queryKey: queryKeys.logs.benchmarks(),
    queryFn: async ({ pageParam }) => unwrap(await logsService.benchmarks(pageParam)),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
}

/**
 * THE CHART'S AND THE STAT CARDS' WINDOW. One bounded read, deliberately NOT the first page
 * of the list above: the chart spans seven weeks, a page spans twenty rows, and a chart whose
 * bars grow as somebody scrolls the list under it says nothing at all. Both live under
 * `logs.all`, so one invalidation still refreshes the pair.
 */
/**
 * ONE LOGGED SESSION, WHOLE — what the screen a history row opens reads.
 *
 * `staleTime: Infinity`: a finished session does not change. The one thing on it that CAN —
 * the heart — invalidates `logs.all`, which is a prefix of this key, so it refreshes with
 * everything else rather than needing its own rule.
 */
export function useSessionLog(id: string) {
  return useQuery({
    queryKey: queryKeys.logs.detail(id),
    queryFn: async () => unwrap(await logsService.detail(id)),
    staleTime: Infinity,
  });
}

/** A year of training days, for the grid. One request; the grid fills its own gaps. */
export function useLogActivity() {
  return useQuery({
    queryKey: queryKeys.logs.activity(),
    queryFn: async () => unwrap(await logsService.activity()),
  });
}

export function useSessionLogTrend() {
  return useQuery({
    queryKey: queryKeys.logs.trend(),
    queryFn: async () => unwrap(await logsService.trend()),
  });
}

/** Every loaded page, flattened — what a screen actually renders. */
export function flattenLogPages(
  data: InfiniteData<SessionLogPageModel> | undefined,
): SessionLogSummaryModel[] {
  return data?.pages.flatMap((page) => page.items) ?? [];
}

/**
 * CLOSING THE SESSION — the debrief's write, and the last thing that happens to a session.
 *
 * ⚠️ NOT OPTIMISTIC, and for the same reason `useStartSession` is not. There is no list on
 * screen for a guessed row to appear in: the debrief's success is a NAVIGATION away from
 * itself, and the id it would need to guess is one only the service can produce. An optimistic
 * write here would buy nothing and could strand the athlete on a screen for a session that was
 * never recorded — on the one screen whose entire job is to record it.
 *
 * WHAT IS INVALIDATED, AND WHY IT IS TWO THINGS:
 *
 * · `logs.all` — history and the benchmarks both just gained a row. The prefix reaches the
 *   list, the benchmarks and anything either half adds beneath them, which is what the
 *   registry's nesting is FOR.
 * · `sessions.suggestions()` — the deck. This is the one that is easy to miss and it is in the
 *   brief for a reason: the engine draws from what the athlete has actually done, so the deck
 *   the debrief returns to is drawn against evidence that did not exist a second ago. Left
 *   stale, the athlete lands on cards computed as though today never happened.
 *
 * IN `onSettled`, NEVER `onSuccess`: a failed write still leaves the cache holding a guess,
 * and a debrief that failed halfway is precisely when the deck must not be trusted.
 */
export function useCreateSessionLog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.logs.create(),
    mutationFn: async (payload: CreateSessionLogPayload) =>
      unwrap(await logsService.create(payload)),

    /* On the OUTCOME and nowhere else. The tap itself is acknowledged by the button. */
    onSuccess: () => haptics.success(),
    onError: () => haptics.error(),

    async onSettled() {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.logs.all }),
        queryClient.invalidateQueries({ queryKey: queryKeys.sessions.suggestions() }),
        /**
         * 🚨 A DEBRIEF CHANGES EVERY MOVEMENT IT TOUCHED, AND THIS WRITE DOES NOT KNOW WHICH.
         *
         * The payload carries the sets, so in principle the movements could be named — but the
         * session ALSO writes derived lines for every movement in the metcon, and a list built
         * here would be a second reading of the same arithmetic that decides them. Invalidating
         * the whole namespace is one line that cannot be wrong; the alternative is a list that
         * is right until somebody changes how derived lines are built, and then leaves a
         * movement chart quietly showing a total that excludes the session the athlete just
         * finished. That is the failure this whole registry exists to prevent.
         *
         * It is deliberately NOT nested under `logs.all`: a movement's history is an aggregate
         * across logs, not a log, and one invalidation covering both would either be far too
         * broad from the log side or miss this from the movement side.
         */
        queryClient.invalidateQueries({ queryKey: queryKeys.movements.all }),
      ]);
    },
  });
}

/**
 * THE HEART — "save as benchmark", which is favouriting under the name the design uses.
 *
 * ONE HOOK FOR BOTH DIRECTIONS, taking the state being MOVED TO rather than two hooks whose
 * only difference is a verb. Both directions invalidate the same thing and report under the
 * same name, and a benchmarks list that refreshed on adding but not on removing is exactly the
 * bug two hooks invite.
 *
 * ⚠️ THE DEBRIEF DOES NOT CALL THIS BEFORE THE LOG EXISTS. A favourite is a join row against a
 * log id, and during the debrief there is no log yet — the pill is a local intention until
 * "Finish session" mints the id. See `Debrief.tsx`, which says what happens if the second call
 * fails after the first succeeded.
 *
 * ═══ OPTIMISTIC SINCE BUD-290, AND ONLY BECAUSE A LIST NOW EXISTS ═══
 *
 * On the debrief there was nothing on screen for a guess to appear in, so there was nothing to
 * be optimistic WITH. History is a list of hearts: a mark that does not fill until the round
 * trip lands — seconds, on a gym's wifi — reads as a dead control and gets tapped again. The
 * rollback below is the other half of that bargain, and it restores every cache the press
 * touched. Nothing here words the failure: the query cache stays the one authority.
 */
export function useFavouriteLog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.logs.favourite(),
    mutationFn: async ({ id, isFavourite }: { id: string; isFavourite: boolean }) =>
      unwrap(
        isFavourite ? await logsService.favourite(id) : await logsService.unfavourite(id),
      ),

    async onMutate({ id, isFavourite }) {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: queryKeys.logs.list() }),
        queryClient.cancelQueries({ queryKey: queryKeys.logs.benchmarks() }),
        queryClient.cancelQueries({ queryKey: queryKeys.logs.trend() }),
      ]);

      const previous = {
        list: queryClient.getQueryData<InfiniteData<SessionLogPageModel>>(queryKeys.logs.list()),
        benchmarks: queryClient.getQueryData<InfiniteData<SessionLogPageModel>>(
          queryKeys.logs.benchmarks(),
        ),
        trend: queryClient.getQueryData<SessionLogPageModel>(queryKeys.logs.trend()),
      };

      queryClient.setQueryData<InfiniteData<SessionLogPageModel>>(
        queryKeys.logs.list(),
        (current) => (current ? mapPages(current, (row) => mark(row, id, isFavourite)) : current),
      );

      /**
       * THE BENCHMARKS LIST IS A FILTER, so releasing the heart REMOVES the row rather than
       * greying it — a list of hearted sessions containing one that is not hearted is worse
       * than a slow one. Pressing the heart does NOT insert into it: the list is ordered by
       * day and then by id ACROSS pages, and guessing which loaded page a row belongs on is
       * how a row appears twice. The invalidation below fetches it into its real place.
       */
      queryClient.setQueryData<InfiniteData<SessionLogPageModel>>(
        queryKeys.logs.benchmarks(),
        (current) =>
          !current || isFavourite
            ? current
            : mapPages(current, (row) => row, (rows) => rows.filter((row) => row.id !== id)),
      );

      queryClient.setQueryData<SessionLogPageModel>(queryKeys.logs.trend(), (current) =>
        current
          ? { ...current, items: current.items.map((row) => mark(row, id, isFavourite)) }
          : current,
      );

      return previous;
    },

    onError(_error, _variables, context) {
      if (!context) return;
      queryClient.setQueryData(queryKeys.logs.list(), context.list);
      queryClient.setQueryData(queryKeys.logs.benchmarks(), context.benchmarks);
      queryClient.setQueryData(queryKeys.logs.trend(), context.trend);
    },

    async onSettled() {
      await queryClient.invalidateQueries({ queryKey: queryKeys.logs.all });
    },
  });
}

function mark(
  row: SessionLogSummaryModel,
  id: string,
  isFavourite: boolean,
): SessionLogSummaryModel {
  return row.id === id ? { ...row, isFavourite } : row;
}

function mapPages(
  data: InfiniteData<SessionLogPageModel>,
  each: (row: SessionLogSummaryModel) => SessionLogSummaryModel,
  whole: (rows: SessionLogSummaryModel[]) => SessionLogSummaryModel[] = (rows) => rows,
): InfiniteData<SessionLogPageModel> {
  return {
    ...data,
    pages: data.pages.map((page) => ({ ...page, items: whole(page.items.map(each)) })),
  };
}
